import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Users, FileText, CheckCircle, Activity, TrendingUp, Calendar, DollarSign } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_venture-mentor-2/artifacts/52nzs5st_ChatGPT%20Image%20Jan%2022%2C%202026%2C%2002_22_56%20PM.png";

const COLORS = ['#0047AB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminKey, setAdminKey] = useState("");
  const [stats, setStats] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [evaluationAnalytics, setEvaluationAnalytics] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("daily");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = () => {
    if (!adminKey) {
      toast.error("Please enter admin key");
      return;
    }
    localStorage.setItem("admin_key", adminKey);
    setIsAuthenticated(true);
    loadAllData(adminKey);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_key");
    setIsAuthenticated(false);
    setAdminKey("");
  };

  useEffect(() => {
    const savedKey = localStorage.getItem("admin_key");
    if (savedKey) {
      setAdminKey(savedKey);
      setIsAuthenticated(true);
      loadAllData(savedKey);
    }
  }, []);

  const loadAllData = async (key) => {
    setIsLoading(true);
    try {
      const [statsRes, timeSeriesRes, evalRes, revenueRes] = await Promise.all([
        axios.get(`${API}/admin/stats?admin_key=${key}`),
        axios.get(`${API}/admin/analytics/time-series?admin_key=${key}&period=${selectedPeriod}`),
        axios.get(`${API}/admin/analytics/evaluations?admin_key=${key}`),
        axios.get(`${API}/admin/analytics/revenue?admin_key=${key}`)
      ]);
      setStats(statsRes.data);
      setTimeSeries(timeSeriesRes.data);
      setEvaluationAnalytics(evalRes.data);
      setRevenueAnalytics(revenueRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Invalid admin key");
        handleLogout();
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const changePeriod = async (period) => {
    setSelectedPeriod(period);
    try {
      const res = await axios.get(`${API}/admin/analytics/time-series?admin_key=${adminKey}&period=${period}`);
      setTimeSeries(res.data);
    } catch (error) {
      toast.error("Failed to update period");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full px-6">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="9Mantra" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-3xl font-normal text-[#1A1A1A] mb-2">Admin Dashboard</h1>
            <p className="text-[#64748B]">Enter admin key to access analytics</p>
          </div>
          <div className="space-y-4">
            <Input
              type="password"
              placeholder="Admin Key"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className="border-[#E2E8F0]"
            />
            <Button
              onClick={handleLogin}
              className="w-full bg-[#0047AB] hover:bg-[#003580] text-white"
            >
              Access Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 animate-spin text-[#0047AB] mx-auto mb-4" />
          <p className="text-[#64748B]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="9Mantra" className="h-10 w-auto" />
            <div>
              <h1 className="text-2xl font-normal text-[#1A1A1A]">9Mantra Admin</h1>
              <p className="text-sm text-[#64748B]">Analytics Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/")} variant="outline" className="border-[#E2E8F0]">
              View Site
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-[#E2E8F0]">
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-[#E2E8F0] p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="h-8 w-8 text-[#0047AB]" />
              <span className="text-3xl font-semibold text-[#1A1A1A]">{stats.totals.users}</span>
            </div>
            <p className="text-[#64748B]">Total Users</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6">
            <div className="flex items-center justify-between mb-2">
              <FileText className="h-8 w-8 text-[#10B981]" />
              <span className="text-3xl font-semibold text-[#1A1A1A]">{stats.totals.sessions}</span>
            </div>
            <p className="text-[#64748B]">Total Sessions</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="h-8 w-8 text-[#F59E0B]" />
              <span className="text-3xl font-semibold text-[#1A1A1A]">{stats.totals.evaluations}</span>
            </div>
            <p className="text-[#64748B]">Total Evaluations</p>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6">
            <div className="flex items-center justify-between mb-2">
              <Activity className="h-8 w-8 text-[#EF4444]" />
              <span className="text-3xl font-semibold text-[#1A1A1A]">{stats.sessions.active}</span>
            </div>
            <p className="text-[#64748B]">Active Sessions</p>
          </div>
        </div>

        {/* Session Status */}
        <div className="bg-white border border-[#E2E8F0] p-6 mb-8">
          <h2 className="text-xl text-[#1A1A1A] mb-6">Session Status</h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-2xl font-semibold text-[#0047AB]">{stats.sessions.active}</p>
              <p className="text-sm text-[#64748B]">Active (In Progress)</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#10B981]">{stats.sessions.completed}</p>
              <p className="text-sm text-[#64748B]">Completed (Ready)</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-[#F59E0B]">{stats.sessions.evaluated}</p>
              <p className="text-sm text-[#64748B]">Evaluated</p>
            </div>
          </div>
        </div>

        {/* Time Series Chart */}
        <div className="bg-white border border-[#E2E8F0] p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl text-[#1A1A1A]">Activity Over Time</h2>
            <div className="flex gap-2">
              {['daily', 'weekly', 'monthly'].map(period => (
                <Button
                  key={period}
                  onClick={() => changePeriod(period)}
                  variant={selectedPeriod === period ? "default" : "outline"}
                  className={selectedPeriod === period ? "bg-[#0047AB] text-white" : "border-[#E2E8F0]"}
                  size="sm"
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </Button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeSeries?.sessions || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" stroke="#64748B" />
              <YAxis stroke="#64748B" />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" name="Sessions" stroke="#0047AB" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Evaluation Analytics */}
        {evaluationAnalytics && evaluationAnalytics.total_evaluations > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Score Distribution */}
              <div className="bg-white border border-[#E2E8F0] p-6">
                <h2 className="text-xl text-[#1A1A1A] mb-6">Score Distribution</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={evaluationAnalytics.score_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="range" stroke="#64748B" />
                    <YAxis stroke="#64748B" />
                    <Tooltip />
                    <Bar dataKey="count" fill="#0047AB" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Average by Evangelist */}
              <div className="bg-white border border-[#E2E8F0] p-6">
                <h2 className="text-xl text-[#1A1A1A] mb-6">Average Score by Evangelist</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={evaluationAnalytics.average_by_evangelist} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} stroke="#64748B" />
                    <YAxis dataKey="evangelist" type="category" width={120} stroke="#64748B" style={{fontSize: '11px'}} />
                    <Tooltip />
                    <Bar dataKey="average_score" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Success Predictor Stats */}
            <div className="bg-white border border-[#E2E8F0] p-6 mb-8">
              <h2 className="text-xl text-[#1A1A1A] mb-6">Success Predictor Statistics</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-3xl font-semibold text-[#0047AB]">
                    {Math.round(evaluationAnalytics.success_predictor_stats.average_survival_days)}
                  </p>
                  <p className="text-sm text-[#64748B]">Average Survival (Days)</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-[#10B981]">
                    {evaluationAnalytics.success_predictor_stats.average_survival_years.toFixed(1)}
                  </p>
                  <p className="text-sm text-[#64748B]">Average Survival (Years)</p>
                </div>
                <div>
                  <p className="text-3xl font-semibold text-[#F59E0B]">
                    {evaluationAnalytics.average_total_score.toFixed(0)}
                  </p>
                  <p className="text-sm text-[#64748B]">Average Total Score (/900)</p>
                </div>
              </div>
              
              {evaluationAnalytics.success_predictor_stats.confidence_distribution && (
                <div className="mt-6">
                  <p className="text-sm text-[#64748B] mb-3">Confidence Level Distribution:</p>
                  <div className="flex gap-4">
                    {Object.entries(evaluationAnalytics.success_predictor_stats.confidence_distribution).map(([level, count]) => (
                      <div key={level} className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          level === 'High' ? 'bg-[#10B981]' : 
                          level === 'Medium' ? 'bg-[#F59E0B]' : 'bg-[#EF4444]'
                        }`} />
                        <span className="text-sm text-[#1A1A1A]">{level}: {count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Revenue Placeholder */}
        <div className="bg-white border border-[#E2E8F0] p-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="h-6 w-6 text-[#10B981]" />
            <h2 className="text-xl text-[#1A1A1A]">Revenue Metrics</h2>
          </div>
          <div className="text-center py-8">
            <p className="text-[#64748B] mb-2">Monetization not yet implemented</p>
            <p className="text-sm text-[#64748B]">All users currently on free tier: {revenueAnalytics?.subscriptions.free || 0} users</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;